/**
 * The one place a `Membership` / `Pass` row becomes a `FormattedSubscriptionPlan`.
 *
 * There used to be three near-copies of this mapping — `usePreselectedPlan`'s
 * pair, `SubscriptionSelectionStep`'s partial pair, and the hand-widening in
 * that step's click handlers — and they had already drifted (the step defaulted
 * `color` a step later than the hook did). Adding the unlisted-plan fetch would
 * have made four producers of the same shape, and four ways for Review to show a
 * different plan depending on how you got there.
 *
 * @module subscription/utils/formatPlan
 */

import type { Membership } from '@lgc/database/types/domains/membership';
import type { Pass } from '@lgc/database/types/domains/pass';
import type { FormattedSubscriptionPlan } from '../types';

/** Money arrives as a decimal STRING in dollars; never assume a number. */
function toDollars(value: unknown): number {
  if (typeof value === 'string') return parseFloat(value) || 0;
  if (typeof value === 'number') return value;
  return 0;
}

const money = (value: unknown): string => `$${toDollars(value).toFixed(2)}`;

/**
 * A membership row → the shape every step renders.
 *
 * `validityDays` is deliberately absent: a membership's term is expressed by
 * `neverEnds` plus its billing frequency, and the one copy that emitted it was
 * reading a field memberships do not carry.
 */
export function formatMembershipPlan(membership: Membership): FormattedSubscriptionPlan {
  const setupFee = toDollars(membership.setupFee);

  return {
    id: membership.id,
    name: membership.name,
    description: membership.description ?? '',
    price: money(membership.price),
    setupFee: setupFee > 0 ? `$${setupFee.toFixed(2)}` : undefined,
    billingFrequency:
      (membership.billingFrequency as FormattedSubscriptionPlan['billingFrequency']) ?? undefined,
    color: membership.color ?? 'purple',
    subscriptionType: 'membership',
    keyBenefits: membership.keyBenefits,
    maxMinutesPerDay: membership.maxMinutesPerDay,
    maxMinutesPerWeek: membership.maxMinutesPerWeek,
    maxMinutesPerMonth: membership.maxMinutesPerMonth,
    maxActiveMinutes: membership.maxActiveMinutes,
    maxActiveSessions: membership.maxActiveSessions,
    neverEnds: membership.neverEnds,
    isFree: membership.isFree,
  };
}

/** A pass row → the shape every step renders. */
export function formatPassPlan(pass: Pass): FormattedSubscriptionPlan {
  return {
    id: pass.id,
    name: pass.name,
    description: pass.description ?? '',
    price: money(pass.price),
    validityDays: pass.validityDays,
    color: pass.color ?? 'purple',
    subscriptionType: 'pass',
    keyBenefits: pass.keyBenefits,
    maxMinutesPerDay: pass.maxMinutesPerDay,
    maxMinutesPerWeek: pass.maxMinutesPerWeek,
    maxMinutesPerMonth: pass.maxMinutesPerMonth,
    maxActiveMinutes: pass.maxActiveMinutes,
    maxActiveSessions: pass.maxActiveSessions,
    maxTotalMinutes: pass.maxTotalMinutes,
  };
}
